import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import {
  Award, BookOpen, Code, Cpu, Terminal, Zap, CheckCircle, Trophy,
  TrendingUp, Layers, RefreshCw, Sparkles, Activity, Shield, ArrowUpRight, Info
} from 'lucide-react';
import CardInfoTooltip from './CardInfoTooltip';
import { useFeatures } from './FeatureGate';
import './StudentAnalyticsDashboard.css';

const StudentAnalyticsDashboard = ({ profile, onExploreSessionsClick, onLMSClick }) => {
  const { isSubEnabled } = useFeatures();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'skills', 'tools', 'projects'
  const [activeRadarSeries, setActiveRadarSeries] = useState(null);

  const availableMonths = useMemo(() => {
    if (!profile?.assessment_attempts_history) return [];
    const months = new Set();
    profile.assessment_attempts_history.forEach(h => {
      if (h.knowledge_scores_snapshot) {
        const d = new Date(h.iso_date);
        const mStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        months.add(mStr);
      }
    });
    return Array.from(months).sort().reverse();
  }, [profile]);

  const [selectedCalendarMonth, setSelectedCalendarMonth] = useState(availableMonths[0] || '');

  useEffect(() => {
    if (!selectedCalendarMonth && availableMonths.length > 0) {
      setSelectedCalendarMonth(availableMonths[0]);
    }
  }, [availableMonths, selectedCalendarMonth]);

  const profileId = profile?.id || profile?.user_id;

  useEffect(() => {
    fetchAnalytics();
  }, [profileId]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [res, coursesRes, marksRes] = await Promise.all([
        api.get('/student/analytics/dashboard', { params: { _t: Date.now() } }),
        api.get('/integrations/edvergencex/student-courses').catch(() => ({ data: { data: [] } })),
        api.get('/student/courses-and-marks').catch(() => ({ data: [] }))
      ]);

      if (res.data) {
        let backendCourseProgress = res.data.course_progress || [];
        
        if (backendCourseProgress.length === 0 && coursesRes.data?.data) {
          const marksData = Array.isArray(marksRes.data) ? marksRes.data : [];
          const marksMap = {};
          marksData.forEach(item => {
            const cId = item.selection?.course_id || item.selection?.course?.externalContentRootId;
            if (cId) {
              marksMap[cId] = item;
            }
          });
          
          backendCourseProgress = coursesRes.data.data.map(c => {
            const markItem = marksMap[c.externalContentRootId] || {};
            const test_marks = markItem.marks || [];
            const avgMark = test_marks.length > 0 ? test_marks.reduce((sum, m) => sum + (m.percentage || m.score_percentage || 0), 0) / test_marks.length : 0;
            return {
              courseName: c.title,
              shortName: c.title?.length > 15 ? c.title.substring(0, 14) + '..' : c.title,
              progress: c.progress || 0,
              assessmentScore: Math.round(avgMark),
              attendance: markItem.attendance_percentage || 0
            };
          });
        }

        const enriched = {
          ...res.data,
          kpis: {
            ...res.data.kpis,
            courses_in_progress: backendCourseProgress.length
          },
          course_progress: backendCourseProgress.map(c => ({
            ...c,
            shortName: c.shortName || (c.courseName?.length > 15 ? c.courseName.substring(0, 14) + '..' : c.courseName)
          }))
        };
        setAnalyticsData(enriched);
      }
    } catch (err) {
      console.warn('Backend analytics fetch fallback:', err);
      // Fallback data if endpoint is unauthenticated or initializing
      setAnalyticsData({
        student_name: profile?.full_name || 'Student',
        kpis: {
          overall_skill_score: 0,
          courses_in_progress: 0,
          completed_projects: 0,
          tools_mastered: 0,
          ai_agent_interactions: 0,
          total_learning_hours: 0
        },
        skills_radar: [],
        tools_mastery: [],
        course_progress: [],
        monthly_velocity: [],
        projects_portfolio: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Safe parse assessment attempts history
  const parsedHistory = useMemo(() => {
    let hist = profile?.assessment_attempts_history;
    if (typeof hist === 'string') {
      try { hist = JSON.parse(hist); } catch (e) { hist = []; }
    }
    return Array.isArray(hist) ? hist : [];
  }, [profile]);

  // Build selectable previous attempts list (with date, month, attempt number, and score)
  const previousAttemptOptions = useMemo(() => {
    if (!parsedHistory || parsedHistory.length === 0) return [];
    
    // Filter attempts that have a knowledge score snapshot or score
    const valid = parsedHistory
      .map((h, idx) => ({
        index: idx,
        id: `attempt_${idx}`,
        attemptNumber: h.attempt || idx + 1,
        date: h.date || (h.iso_date ? new Date(h.iso_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : `Attempt ${idx + 1}`),
        monthYear: h.iso_date ? new Date(h.iso_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '',
        iso_date: h.iso_date || '',
        score: h.score,
        snapshot: h.knowledge_scores_snapshot || null
      }))
      .filter(item => item.snapshot && Array.isArray(item.snapshot) && item.snapshot.length > 0);

    return valid;
  }, [parsedHistory]);

  // Selected previous attempt index (defaults to the first attempt if multiple, or earliest)
  const [selectedPreviousId, setSelectedPreviousId] = useState('');

  useEffect(() => {
    if (previousAttemptOptions.length > 0 && !selectedPreviousId) {
      // Default to the first (baseline) attempt so user can immediately see their growth
      setSelectedPreviousId(previousAttemptOptions[0].id);
    }
  }, [previousAttemptOptions, selectedPreviousId]);

  // Toggle view mode: 'side-by-side' (default) vs 'overlay' (radar overlay)
  const [comparisonViewMode, setComparisonViewMode] = useState('side-by-side');

  // Compute rich comparative dataset between Selected Previous Attempt and Current State
  const comparativeEvolution = useMemo(() => {
    if (!profile?.ai_knowledge_scores) {
      return {
        skills: [],
        previousAttemptInfo: null,
        currentAttemptInfo: null,
        avgPrevious: 0,
        avgCurrent: 0,
        netDelta: 0,
        improvedCount: 0,
        newSkillsCount: 0,
        maintainedCount: 0
      };
    }

    const currentScores = Array.isArray(profile.ai_knowledge_scores) ? profile.ai_knowledge_scores : [];
    
    // Find target previous attempt snapshot
    let chosenAttempt = previousAttemptOptions.find(opt => opt.id === selectedPreviousId);
    if (!chosenAttempt && previousAttemptOptions.length > 0) {
      chosenAttempt = previousAttemptOptions[0];
    }

    const previousSnapshot = chosenAttempt?.snapshot || [];
    const prevMap = new Map();
    previousSnapshot.forEach(item => {
      if (item.name) prevMap.set(item.name.toLowerCase().trim(), parseFloat(item.score) || 0);
    });

    const currMap = new Map();
    currentScores.forEach(item => {
      if (item.name) currMap.set(item.name.toLowerCase().trim(), {
        originalName: item.name,
        score: parseFloat(item.score) || 0,
        tested: !!item.tested
      });
    });

    // Combine all unique skill names from both Previous and Current
    const allSkillKeys = Array.from(new Set([...prevMap.keys(), ...currMap.keys()]));

    const skills = [];
    let totalPrev = 0;
    let countPrev = 0;
    let totalCurr = 0;
    let countCurr = 0;
    let improved = 0;
    let newSkills = 0;
    let maintained = 0;

    allSkillKeys.forEach(key => {
      const hasPrev = prevMap.has(key);
      const prevScore = hasPrev ? prevMap.get(key) : null;
      const currEntry = currMap.get(key);
      const hasCurr = !!currEntry;
      const currScore = hasCurr ? currEntry.score : 0;
      const displayName = currEntry?.originalName || (previousSnapshot.find(s => s.name?.toLowerCase().trim() === key)?.name || key);

      if (hasPrev) {
        totalPrev += prevScore;
        countPrev++;
      }
      if (hasCurr && currScore > 0) {
        totalCurr += currScore;
        countCurr++;
      }

      let delta = 0;
      let status = 'maintained'; // 'improved', 'new', 'declined', 'maintained'

      if (!hasPrev && hasCurr) {
        status = 'new';
        delta = currScore;
        newSkills++;
      } else if (hasPrev && hasCurr) {
        delta = +(currScore - prevScore).toFixed(1);
        if (delta > 0) {
          status = 'improved';
          improved++;
        } else if (delta < 0) {
          status = 'declined';
        } else {
          status = 'maintained';
          maintained++;
        }
      }

      skills.push({
        name: displayName,
        previousScore: prevScore !== null ? prevScore : 0,
        hasPrevious: hasPrev,
        currentScore: currScore,
        delta: delta,
        status: status
      });
    });

    // Sort by largest growth first, then new skills, then maintained
    skills.sort((a, b) => {
      if (b.status === 'new' && a.status !== 'new') return -1;
      if (a.status === 'new' && b.status !== 'new') return 1;
      return b.delta - a.delta;
    });

    const avgPrev = countPrev > 0 ? +(totalPrev / countPrev).toFixed(1) : 0;
    const avgCurr = countCurr > 0 ? +(totalCurr / countCurr).toFixed(1) : 0;
    const netGrowth = +(avgCurr - avgPrev).toFixed(1);

    return {
      skills,
      previousAttemptInfo: chosenAttempt || null,
      currentAttemptInfo: {
        date: 'Today (Latest Attempt)',
        score: profile?.last_assessment_score ?? Math.round(avgCurr * 10)
      },
      avgPrevious: avgPrev,
      avgCurrent: avgCurr,
      netDelta: netGrowth,
      improvedCount: improved,
      newSkillsCount: newSkills,
      maintainedCount: maintained
    };
  }, [profile, selectedPreviousId, previousAttemptOptions]);

  // Radar data (top 6 major skills for dual side-by-side radar charts)
  const radarComparisonData = useMemo(() => {
    return comparativeEvolution.skills.slice(0, 6).map(s => ({
      name: s.name.length > 10 ? s.name.substring(0, 9) + '..' : s.name,
      fullName: s.name,
      previous: s.hasPrevious ? s.previousScore : 0,
      current: s.currentScore
    }));
  }, [comparativeEvolution]);

  if (loading || !analyticsData) {
    return (
      <div className="student-analytics-wrapper" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <RefreshCw className="spin-animation" size={32} style={{ margin: '0 auto 12px', color: '#3b82f6' }} />
          <p style={{ fontSize: '15px', fontWeight: 600 }}>Loading Analytics</p>
        </div>
      </div>
    );
  }

  const { kpis, skills_radar, tools_mastery, course_progress, monthly_velocity, projects_portfolio } = analyticsData;

  return (
    <div className="student-analytics-wrapper">
      {/* Top Hero Bar */}
      <header className="analytics-hero-header">
        <div className="analytics-hero-title">
          <h1>Welcome back, {analyticsData.student_name || 'Student'}! 👋</h1>
          <p>AI Intelligence & Career Proficiency Analytics Dashboard</p>
        </div>
        <div className="analytics-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button className="analytics-action-btn secondary" onClick={fetchAnalytics}>
            <RefreshCw size={15} /> Refresh Analytics
          </button>
          <button className="analytics-action-btn primary" onClick={onLMSClick}>
            <BookOpen size={16} /> Open LMS Courses
          </button>
        </div>
      </header>

      {/* Hero KPI Summary Grid */}
      <section className="analytics-kpi-grid">
        <div className="analytics-kpi-card" style={{ '--card-accent': '#3b82f6' }}>
          <CardInfoTooltip title="Overall Skill Mastery" content="Aggregate proficiency score derived from completed LMS assessments and verified technical projects." />
          <div>
            <div className="kpi-info-label">
              Overall Skill Mastery
            </div>
            <div className="kpi-info-value">{kpis?.overall_skill_score ?? 0}%</div>
            <div className="kpi-info-sub">
              <TrendingUp size={14} /> Top 10% Cohort Proficiency
            </div>
          </div>
          <div className="kpi-icon-box">
            <Trophy size={26} />
          </div>
        </div>

        <div className="analytics-kpi-card" style={{ '--card-accent': '#10b981' }}>
          <CardInfoTooltip title="Course Progress" content="Tracks the number of active LMS courses assigned to you and your average completion rate." />
          <div>
            <div className="kpi-info-label">
              Course Progress
            </div>
            <div className="kpi-info-value">{kpis?.courses_in_progress ?? 0} Assigned</div>
            <div className="kpi-info-sub">
              <CheckCircle size={14} /> Avg 85% Completion Rate
            </div>
          </div>
          <div className="kpi-icon-box" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#34d399' }}>
            <BookOpen size={26} />
          </div>
        </div>

        <div className="analytics-kpi-card" style={{ '--card-accent': '#8b5cf6' }}>
          <CardInfoTooltip title="Projects & Portfolio" content="Total verified artifacts built and submitted via integrated GitHub repositories or assignments." />
          <div>
            <div className="kpi-info-label">
              Projects & Portfolio
            </div>
            <div className="kpi-info-value">{kpis?.completed_projects ?? 0} Built</div>
            <div className="kpi-info-sub" style={{ color: '#a78bfa' }}>
              <Code size={14} /> Verified GitHub Artifacts
            </div>
          </div>
          <div className="kpi-icon-box" style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#a78bfa' }}>
            <Terminal size={26} />
          </div>
        </div>

        <div className="analytics-kpi-card" style={{ '--card-accent': '#ec4899' }}>
          <CardInfoTooltip title="AI Token Usage" content="Your usage of OpenRouter AI tokens against the system limit." />
          <div>
            <div className="kpi-info-label">
              AI Token Usage
            </div>
            <div className="kpi-info-value">{kpis?.ai_tokens_used?.toLocaleString() ?? 0} Used</div>
            <div className="kpi-info-sub" style={{ color: '#f472b6' }}>
              <Activity size={14} /> Remaining: {Math.max(0, ((kpis?.ai_token_limit ?? 0) + (kpis?.extra_ai_token_allowance ?? 0)) - (kpis?.ai_tokens_used ?? 0)).toLocaleString()}
            </div>
          </div>
          <div className="kpi-icon-box" style={{ background: 'rgba(236, 72, 153, 0.12)', color: '#ec4899' }}>
            <Activity size={26} />
          </div>
        </div>
      </section>

      {/* Skill Evolution Comparison (Side-by-Side: Previous Attempt vs Current Attempt) */}
      <section className="analytics-charts-grid" style={{ gridTemplateColumns: '1fr', marginBottom: '24px' }}>
        <div className="analytics-chart-panel" style={{ padding: '26px' }}>
          
          {/* Header Bar */}
          <div className="chart-panel-header" style={{ marginBottom: '22px', flexWrap: 'wrap', gap: '16px' }}>
            <div className="chart-panel-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a' }}>Skill Evolution Timeline</h3>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '3px 8px', borderRadius: '12px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                  Side-by-Side
                </span>
              </div>
              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Compare Previous Assessment / Resume Attempts with Your Current Proficiency</span>
            </div>

            {/* Filter and View Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {/* Previous Attempt Selector */}
              <div className="evolution-filters" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '6px 14px', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                <span style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  Compare Previous Attempt:
                </span>
                <select 
                  value={selectedPreviousId}
                  onChange={(e) => setSelectedPreviousId(e.target.value)}
                  style={{ 
                    background: '#ffffff', 
                    border: '1.5px solid #2563eb', 
                    borderRadius: '8px', 
                    padding: '6px 12px', 
                    fontSize: '0.85rem', 
                    color: '#0f172a', 
                    fontWeight: 600, 
                    outline: 'none', 
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(37,99,235,0.1)'
                  }}
                >
                  {previousAttemptOptions.length > 0 ? (
                    previousAttemptOptions.map((opt, i) => (
                      <option key={opt.id} value={opt.id}>
                        Attempt {opt.attemptNumber} • {opt.date} {opt.score ? `(${opt.score}%)` : ''}
                      </option>
                    ))
                  ) : (
                    <option value="">Attempt 1 (Baseline)</option>
                  )}
                </select>
              </div>

              {/* View Mode Toggle Button */}
              <div style={{ display: 'flex', background: '#f1f5f9', padding: '3px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <button
                  type="button"
                  onClick={() => setComparisonViewMode('side-by-side')}
                  style={{
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: comparisonViewMode === 'side-by-side' ? '#2563eb' : 'transparent',
                    color: comparisonViewMode === 'side-by-side' ? '#ffffff' : '#64748b',
                    transition: 'all 0.15s ease'
                  }}
                >
                  Side-by-Side View
                </button>
                <button
                  type="button"
                  onClick={() => setComparisonViewMode('overlay')}
                  style={{
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: comparisonViewMode === 'overlay' ? '#2563eb' : 'transparent',
                    color: comparisonViewMode === 'overlay' ? '#ffffff' : '#64748b',
                    transition: 'all 0.15s ease'
                  }}
                >
                  Overlay Radar
                </button>
              </div>
            </div>
          </div>
          
          {comparativeEvolution.skills.length === 0 ? (
            <div className="evolution-empty-state" style={{ padding: '40px', textAlign: 'center', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '12px' }}>
              <p style={{ color: '#64748b', margin: 0, fontWeight: 500 }}>
                Upload your resume, sync your GitHub, and complete an assessment to establish your proficiency baseline! Your progression comparison will appear here.
              </p>
            </div>
          ) : (
            <div>
              {/* Executive Growth KPI Banner */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '14px',
                padding: '16px 20px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)',
                borderRadius: '14px',
                border: '1px solid #dbeafe',
                marginBottom: '24px'
              }}>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Selected Previous Attempt
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#ea580c', marginTop: '2px' }}>
                    {comparativeEvolution.previousAttemptInfo ? `Attempt ${comparativeEvolution.previousAttemptInfo.attemptNumber} (${comparativeEvolution.previousAttemptInfo.date})` : 'Initial Baseline'}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#9a3412', fontWeight: 600, marginTop: '2px' }}>
                    Avg Score: {comparativeEvolution.avgPrevious} / 10
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Current Updated Attempt
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#2563eb', marginTop: '2px' }}>
                    Latest (Updated Resume / GitHub)
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#1d4ed8', fontWeight: 600, marginTop: '2px' }}>
                    Avg Score: {comparativeEvolution.avgCurrent} / 10
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Net Growth Velocity
                  </div>
                  <div style={{
                    fontSize: '1.25rem',
                    fontWeight: 800,
                    marginTop: '2px',
                    color: comparativeEvolution.netDelta > 0 ? '#16a34a' : (comparativeEvolution.netDelta < 0 ? '#dc2626' : '#64748b')
                  }}>
                    {comparativeEvolution.netDelta > 0 ? `+${comparativeEvolution.netDelta}` : comparativeEvolution.netDelta} pts
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 600, marginTop: '2px' }}>
                    {comparativeEvolution.improvedCount} skill{comparativeEvolution.improvedCount !== 1 ? 's' : ''} improved
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    New Skills Added
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#7c3aed', marginTop: '2px' }}>
                    +{comparativeEvolution.newSkillsCount}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: '#6d28d9', fontWeight: 600, marginTop: '2px' }}>
                    Verified from updated stack
                  </div>
                </div>
              </div>

              {/* Side-by-Side Dual Radar Charts OR Single Overlay */}
              {comparisonViewMode === 'side-by-side' ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '28px' }}>
                  {/* Left Radar: Previous Attempt */}
                  <div style={{
                    background: '#ffffff',
                    border: '1.5px solid #fed7aa',
                    borderRadius: '16px',
                    padding: '20px',
                    boxShadow: '0 4px 14px rgba(234, 88, 12, 0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ea580c', display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#9a3412' }}>
                          Previous Attempt Shape
                        </span>
                      </div>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#ea580c', background: '#fff7ed', padding: '3px 8px', borderRadius: '6px' }}>
                        {comparativeEvolution.previousAttemptInfo?.date || 'Past Baseline'}
                      </span>
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarComparisonData}>
                        <PolarGrid stroke="#fed7aa" strokeDasharray="3 3" />
                        <PolarAngleAxis dataKey="name" tick={{ fill: '#7c2d12', fontSize: 11, fontWeight: 700 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 10]} tick={false} axisLine={false} />
                        <Radar name="Previous" dataKey="previous" stroke="#ea580c" fill="#f97316" fillOpacity={0.4} strokeWidth={2} />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #fed7aa', fontSize: '12px' }} />
                      </RadarChart>
                    </ResponsiveContainer>
                    <div style={{ fontSize: '0.8rem', color: '#9a3412', fontWeight: 600, textAlign: 'center', marginTop: '4px' }}>
                      Baseline Average: <strong>{comparativeEvolution.avgPrevious} / 10</strong>
                    </div>
                  </div>

                  {/* Right Radar: Current Updated Attempt */}
                  <div style={{
                    background: '#ffffff',
                    border: '1.5px solid #bfdbfe',
                    borderRadius: '16px',
                    padding: '20px',
                    boxShadow: '0 4px 14px rgba(37, 99, 235, 0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#2563eb', display: 'inline-block' }}></span>
                        <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#1d4ed8' }}>
                          Current Updated Shape
                        </span>
                      </div>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '3px 8px', borderRadius: '6px' }}>
                        Latest State (Today)
                      </span>
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarComparisonData}>
                        <PolarGrid stroke="#bfdbfe" strokeDasharray="3 3" />
                        <PolarAngleAxis dataKey="name" tick={{ fill: '#1e3a8a', fontSize: 11, fontWeight: 700 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 10]} tick={false} axisLine={false} />
                        <Radar name="Current" dataKey="current" stroke="#2563eb" fill="#3b82f6" fillOpacity={0.45} strokeWidth={2} />
                        <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #bfdbfe', fontSize: '12px' }} />
                      </RadarChart>
                    </ResponsiveContainer>
                    <div style={{ fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 600, textAlign: 'center', marginTop: '4px' }}>
                      Current Average: <strong>{comparativeEvolution.avgCurrent} / 10</strong>
                    </div>
                  </div>
                </div>
              ) : (
                /* Overlay Mode: Single Chart with Both Radars */
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '20px', marginBottom: '28px' }}>
                  <div style={{ textAlign: 'center', marginBottom: '10px', fontSize: '0.9rem', fontWeight: 700, color: '#334155' }}>
                    Radar Overlay: <span style={{ color: '#ea580c' }}>Previous Attempt (Orange)</span> vs <span style={{ color: '#2563eb' }}>Current Attempt (Blue)</span>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarComparisonData}>
                      <PolarGrid stroke="#e2e8f0" />
                      <PolarAngleAxis dataKey="name" tick={{ fill: '#475569', fontSize: 12, fontWeight: 600 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 10]} tick={false} axisLine={false} />
                      <Radar name="Previous Attempt" dataKey="previous" stroke="#ea580c" fill="#f97316" fillOpacity={0.25} />
                      <Radar name="Current Attempt" dataKey="current" stroke="#2563eb" fill="#3b82f6" fillOpacity={0.4} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                      <Legend wrapperStyle={{ fontSize: '13px', fontWeight: 600, paddingTop: '10px' }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Side-by-Side Comparative Skill Cards Grid */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
                    Skill Progression Matrix ({comparativeEvolution.skills.length} skills analyzed)
                  </h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.78rem', fontWeight: 600 }}>
                    <span style={{ color: '#16a34a' }}>● Improved ({comparativeEvolution.improvedCount})</span>
                    <span style={{ color: '#7c3aed' }}>★ New Skills ({comparativeEvolution.newSkillsCount})</span>
                    <span style={{ color: '#64748b' }}>• Maintained ({comparativeEvolution.maintainedCount})</span>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px' }}>
                  {comparativeEvolution.skills.map((sk, idx) => {
                    const isNew = sk.status === 'new';
                    const isImproved = sk.status === 'improved';
                    const isDeclined = sk.status === 'declined';

                    return (
                      <div
                        key={idx}
                        style={{
                          background: '#ffffff',
                          borderRadius: '12px',
                          padding: '12px 14px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          border: isNew ? '1.5px solid #ddd6fe' : (isImproved ? '1.5px solid #bbf7d0' : '1px solid #e2e8f0'),
                          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                          position: 'relative'
                        }}
                      >
                        {/* Top: Skill Name + Delta Pill */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span
                            style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '65%' }}
                            title={sk.name}
                          >
                            {sk.name}
                          </span>

                          <span style={{
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: '12px',
                            ...(isNew ? { background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' } :
                               isImproved ? { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' } :
                               isDeclined ? { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' } :
                               { background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' })
                          }}>
                            {isNew ? '★ NEW' : (sk.delta > 0 ? `+${sk.delta}` : `${sk.delta}`)}
                          </span>
                        </div>

                        {/* Middle: Side-by-Side Comparison Blocks */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                          {/* Previous Score Box */}
                          <div style={{ background: '#fff7ed', borderRadius: '8px', padding: '6px 8px', textAlign: 'center', border: '1px solid #ffedd5' }}>
                            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#9a3412', textTransform: 'uppercase' }}>Previous</div>
                            <div style={{ fontSize: '1rem', fontWeight: 800, color: sk.hasPrevious ? '#ea580c' : '#94a3b8' }}>
                              {sk.hasPrevious ? sk.previousScore : '—'}
                            </div>
                          </div>

                          <div style={{ color: '#94a3b8', fontSize: '0.9rem', fontWeight: 700 }}>➔</div>

                          {/* Current Score Box */}
                          <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '6px 8px', textAlign: 'center', border: '1px solid #dbeafe' }}>
                            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase' }}>Current</div>
                            <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#2563eb' }}>
                              {sk.currentScore}
                            </div>
                          </div>
                        </div>

                        {/* Bottom: Progress Bar */}
                        <div style={{ width: '100%', height: '5px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden', marginTop: '2px' }}>
                          <div
                            style={{
                              width: `${Math.min(100, (sk.currentScore / 10) * 100)}%`,
                              height: '100%',
                              background: isNew ? 'linear-gradient(90deg, #8b5cf6, #7c3aed)' : (isImproved ? 'linear-gradient(90deg, #10b981, #059669)' : '#3b82f6'),
                              borderRadius: '4px'
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Main Charts Grid Row 1: Skills Radar Chart & Course Progress Bar Chart */}
      <section className="analytics-charts-grid">
        {/* Radar Chart: Core Competency Dimensions */}
        <div className="analytics-chart-panel">
          <CardInfoTooltip title="Core Competency" content="A radar breakdown of your skills across 6 primary engineering axes based on LMS grades and verified projects." />
          <div className="chart-panel-header">
            <div className="chart-panel-title">
              <h3>Core Competency Proficiency Radar</h3>
              <span>6 Engineering Axes</span>
            </div>
            <span className="chart-badge">AI Skill Index</span>
          </div>
          {skills_radar && skills_radar.length > 0 ? (
            <div style={{ width: '100%', height: '310px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart cx="50%" cy="50%" outerRadius="75%" data={skills_radar}>
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="subject" stroke="#475569" tick={{ fill: '#334155', fontSize: 12, fontWeight: 700 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#cbd5e1" />
                  <Radar name="Proficiency Score" dataKey="A" stroke="#2563eb" fill="#3b82f6" fillOpacity={0.35} />
                  <Tooltip
                    contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', color: '#0f172a', boxShadow: '0 8px 20px rgba(15,23,42,0.08)' }}
                    labelStyle={{ color: '#475569', fontWeight: 700 }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: '310px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#64748b', padding: '20px' }}>
              <Trophy size={36} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
              <p style={{ fontWeight: 600, fontSize: '14px', color: '#475569', marginBottom: '4px' }}>No Competency Data Yet</p>
              <p style={{ fontSize: '12px', maxWidth: '320px' }}>Complete your Profile skills to generate your competency proficiency radar.</p>
            </div>
          )}
        </div>

        {/* Bar Chart: Course Progress & Assessment Performance */}
        <div className="analytics-chart-panel">
          <CardInfoTooltip title="Course Progress" content="Compares your completion progress versus your actual assessment scores for enrolled LMS courses." />
          <div className="chart-panel-header">
            <div className="chart-panel-title">
              <h3>Course Progress & Assessment Scores</h3>
              <span>Assigned Courses</span>
            </div>
            <span className="chart-badge">Performance vs Completion</span>
          </div>
          {course_progress && course_progress.length > 0 ? (
            <div style={{ width: '100%', height: '310px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={course_progress} margin={{ top: 20, right: 15, left: -15, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="shortName" stroke="#64748b" tick={{ fill: '#334155', fontSize: 11.5, fontWeight: 700 }} interval={0} />
                  <YAxis stroke="#64748b" domain={[0, 100]} tick={{ fill: '#475569', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', color: '#0f172a', boxShadow: '0 8px 20px rgba(15,23,42,0.08)' }}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.courseName || label}
                  />
                  <Legend wrapperStyle={{ color: '#334155', fontSize: '12.5px', fontWeight: 600, paddingTop: '10px' }} />
                  <Bar name="Course Completion (%)" dataKey="progress" fill="#2563eb" radius={[6, 6, 0, 0]} />
                  {isSubEnabled('lms', 'lms_marks_view') && <Bar name="Assessment Score (%)" dataKey="assessmentScore" fill="#10b981" radius={[6, 6, 0, 0]} />}
                  {isSubEnabled('lms', 'lms_attendance_view') && <Bar name="Attendance (%)" dataKey="attendance" fill="#f59e0b" radius={[6, 6, 0, 0]} />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: '310px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#64748b', padding: '20px' }}>
              <BookOpen size={36} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
              <p style={{ fontWeight: 600, fontSize: '14px', color: '#475569', marginBottom: '4px' }}>No Assigned Courses Yet</p>
              <p style={{ fontSize: '12px', maxWidth: '320px' }}>Your university has not assigned any courses to your batch yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Main Charts Grid Row 2: Tools Mastery Breakdown */}
      <section className="analytics-charts-grid" style={{ gridTemplateColumns: '1fr' }}>

        {/* Interactive Tools Mastery Progress Grid */}
        <div className="analytics-chart-panel">
          <CardInfoTooltip title="Tools Mastery" content="Breakdown of specific technologies and your relative proficiency in each." />
          <div className="chart-panel-header">
            <div className="chart-panel-title">
              <h3>Tools & Technology Stack Mastery</h3>
              <span>Verified Proficiency Matrix</span>
            </div>
            <span className="chart-badge">Level Analysis</span>
          </div>
          {tools_mastery && tools_mastery.length > 0 ? (
            <div className="tools-grid-layout">
              {tools_mastery.map((tool, idx) => (
                <div key={idx} className="tool-mastery-card">
                  <div className="tool-header">
                    <div className="tool-name-badge">
                      <div>
                        <div className="tool-name">{tool.name}</div>
                        <div className="tool-category">{tool.category}</div>
                      </div>
                    </div>
                    <span className="tool-level-badge">{tool.level}%</span>
                  </div>
                  <div className="tool-progress-track">
                    <div className="tool-progress-fill" style={{ width: `${tool.level}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ height: '290px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#64748b', padding: '20px' }}>
              <Cpu size={36} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
              <p style={{ fontWeight: 600, fontSize: '14px', color: '#475569', marginBottom: '4px' }}>No Tools Mastered Yet</p>
              <p style={{ fontSize: '12px', maxWidth: '320px' }}>Add technical skills in your Profile to populate your technology stack mastery.</p>
            </div>
          )}
        </div>
      </section>

      {/* Projects Portfolio Section */}
      <div className="analytics-section-title" style={{ position: 'relative' }}>
        <h2 style={{ display: 'flex', alignItems: 'center' }}>
          <Code size={22} color="#60a5fa" />
          Student Engineering Projects Portfolio
        </h2>
        <CardInfoTooltip title="Projects Portfolio" content="A list of practical projects you have completed or are working on." />
        <span style={{ fontSize: '13px', color: '#94a3b8' }}>Hands-On Verification</span>
      </div>

      <section className="projects-portfolio-grid">
        {projects_portfolio && projects_portfolio.length > 0 ? (
          projects_portfolio.slice(0, 6).map((proj) => {
            const targetUrl = proj.repoUrl || proj.url || proj.liveUrl || (profile && profile.github_profile ? `https://github.com/${profile.github_profile.replace('https://github.com/', '').replace('github.com/', '').replace(/^\/+|\/+$/g, '')}` : null);
            const openProjectRepo = (e) => {
              if (e && e.stopPropagation) e.stopPropagation();
              if (targetUrl) {
                window.open(targetUrl, '_blank', 'noopener,noreferrer');
              } else {
                onLMSClick();
              }
            };
            return (
              <div
                key={proj.id}
                className="project-card"
                onClick={openProjectRepo}
                style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }}
                title={targetUrl ? `Click to open ${targetUrl}` : 'View Project Details'}
              >
                <div>
                  <div className="project-header">
                    <div>
                      <h4 className="project-title" style={{ color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {proj.title}
                      </h4>
                      <span className="project-domain">{proj.domain}</span>
                    </div>
                    <div className="project-score-circle" title="Quality Assessment Score">
                      {proj.score}
                    </div>
                  </div>
                  <div className="tech-tags-list">
                    {proj.techStack.map((tech, i) => (
                      <span key={i} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>

                <div className="project-footer">
                  <span className="project-status-tag">
                    <CheckCircle size={15} /> {proj.status}
                  </span>
                  <button
                    className="analytics-action-btn secondary"
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                    onClick={openProjectRepo}
                  >
                    View Artifact <ArrowUpRight size={13} />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ gridColumn: '1 / -1', padding: '40px 20px', textAlign: 'center', background: '#ffffff', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
            <Code size={40} style={{ color: '#94a3b8', margin: '0 auto 12px' }} />
            <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>No Verified Projects Yet</h4>
            <p style={{ fontSize: '13px', color: '#64748b', maxWidth: '440px', margin: '0 auto' }}>
              Connect your GitHub account in the Projects tab or fill out your profile skills to display verified engineering artifacts.
            </p>
          </div>
        )}
      </section>
      
    </div>
  );
};

export default StudentAnalyticsDashboard;
